<script setup lang="ts">
import { api } from '@/api';
import type { TrackItDataSequenceInfoDto, TrackItMeasurementInfoDto } from '@/api/generator';
import PtwBackButton from '@/components/buttons/PtwBackButton.vue';
import Comments from '@/components/comments/Comments.vue';
import { useAmountComments } from '@/components/comments/useAmountComments';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import FormInputDisplay from '@/components/forms/display/FormInputDisplay.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { defaultPageTabs } from '@/components/pages/BasePage/defaults';
import type { TabDefinition } from '@/components/Tabs/types';
import { entityNotFoundErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { fullName } from '@/utils/helper';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import TrackItDataPointsTable from '@/views/jobOccurrences/components/workItems/TrackItDataPointsTable.vue';
import TrackItMeasurementDataTable from '@/views/jobOccurrences/components/workItems/TrackItMeasurementDataTable.vue';
import TrackItMeasurementParametersTable from '@/views/jobOccurrences/components/workItems/TrackItMeasurementParametersTable.vue';
import { useTranslation } from 'i18next-vue';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  id: string;
}>();

const router = useRouter();
const { t } = useTranslation();

const measurement = ref<TrackItMeasurementInfoDto>();

const { commentsTabTitle } = useAmountComments(() => measurement.value ?? {});

const tabs = computed(
  () =>
    ({
      details: {
        title: t('Measurement_one'),
      },
      comments: {
        ...defaultPageTabs.comments,
        title: commentsTabTitle.value,
      },
    }) satisfies TabDefinition,
);
const measurementData = ref<TrackItDataSequenceInfoDto[]>([]);

const { action: loadMeasurement, loading } = useLoading(async () => {
  const { result } = await api.trackItImportApi.getTrackItMeasurementForDetailView(
    { id: Number(props.id) },
    [entityNotFoundErrorHandler(router)],
  );

  measurement.value = result;
}, true);

const { action: loadMeasurementData, loading: measurementDataLoading } = useLoading(async () => {
  const { result } = await api.trackItImportApi.getDataSequencesForMeasurement({
    measurementId: Number(props.id),
  });

  measurementData.value = result ?? [];
}, true);

onMounted(() => Promise.all([loadMeasurement(), loadMeasurementData()]));
</script>

<template>
  <BasePage :loading="loading">
    <template #title>
      <BasePageTitle :title="$t('ImportedMeasurement' as I18nKey)" />
    </template>

    <template #actions>
      <PtwBackButton :to="{ name: routenames.importedMeasurements.overview }" />
    </template>

    <BasePageNavigation :tabs="tabs">
      <template #details>
        <template v-if="measurement">
          <FormSection>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <FormLabel :label="$t('MeasurementDate')">
                  <DateTimeFormatter :value="measurement.measurementDate" />
                </FormLabel>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <FormLabel :label="$t('TrackItAlias')">
                  <FormInputDisplay :value="measurement.radiationUnit" />
                </FormLabel>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <FormLabel :label="$t('TrackItMeasuringDevice')">
                  <FormInputDisplay :value="measurement.measurementDevice" />
                </FormLabel>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <FormLabel :label="$t('TrackItMeasuringSoftware')">
                  <FormInputDisplay :value="measurement.measurementSoftware" />
                </FormLabel>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <FormLabel :label="$t('ImportDate')">
                  <DateTimeFormatter :value="measurement.importDate" />
                </FormLabel>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <FormLabel :label="$t('ImportUser' as I18nKey)">
                  <FormInputDisplay :value="fullName(measurement.importUser ?? undefined)" />
                </FormLabel>
              </v-col>
            </v-row>
          </FormSection>

          <FormSection
            v-if="measurement.measurementParameters?.length || measurement.dataTypes?.length"
          >
            <v-row>
              <v-col v-if="measurement.measurementParameters?.length" cols="12" md="6">
                <TrackItMeasurementParametersTable
                  :title="$t('Parameter_other')"
                  :parameters="measurement.measurementParameters"
                />
              </v-col>

              <v-col v-if="measurement.dataTypes?.length" cols="12" md="6">
                <TrackItDataPointsTable
                  title="Track-It Data Points"
                  :dataPoints="measurement.dataTypes"
                />
              </v-col>
            </v-row>
          </FormSection>

          <FormSection>
            <v-progress-linear v-if="measurementDataLoading" indeterminate />

            <TrackItMeasurementDataTable
              v-else
              :title="$t('TrackItMeasurementData' as I18nKey)"
              :measurementData="measurementData"
            />
          </FormSection>
        </template>
      </template>

      <template #comments>
        <FormSection>
          <Comments
            v-if="measurement?.id"
            :id="measurement.id"
            entityWithCommentType="WorkResult"
            readonly
            :preloadedComments="measurement.comments ?? []"
          />
        </FormSection>
      </template>
    </BasePageNavigation>
  </BasePage>
</template>
